// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {AttrsSchema, Component, jsx} from 'panel';

import customEventsSvg from 'common/assets/images/charts/flows-custom-events.svg';
import collapseRepeatedSvg from 'common/assets/images/charts/flows-collapse-repeated.svg';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {AdvancedQueryOptions} from 'common/report/flows/models/types';
import {SelectEl} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SwitchEl} from 'common/components/switch';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {IronContextRegistry} from 'common/report/context-registry';

import css from './index.inline.styl';

export type AdvancedSettingsItem = {
  option: AdvancedQueryOptions;
  toggled: boolean;
};

type FlowsAdvancedSettingsAttrs = {
  items: AdvancedSettingsItem[];
};

export type FlowsAdvancedSettingsChangeEvent = CustomEvent<AdvancedSettingsItem>;

export interface FlowsAdvancedSettingsJsxProps {
  attrs: FlowsAdvancedSettingsAttrs;
  on: {
    change: (ev: FlowsAdvancedSettingsChangeEvent) => void;
  };
}

export class FlowsAdvancedSettings extends Component<
  EmptyObj,
  FlowsAdvancedSettingsAttrs,
  unknown,
  unknown,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<FlowsAdvancedSettingsAttrs> {
    return {
      ...super.attrsSchema,
      items: {type: `json`, tsType: `AdvancedSettingsItem[]`},
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      contexts: [`i18n` as const],
      template: () => {
        const itemsMap = new Map(this.attr(`items`).map((item) => [item.option, item]));
        const t = this.getContext(`i18n`).t;

        return (
          <SelectEl
            sel='.flows-advanced-settings'
            attrs={{
              theme: SelectTheme.Secondary,
              icon: {leftIcon: `ellipsis`},
              'icon-only': true,
              'selected-option': ``,
            }}
          >
            <div sel='.advanced-options-container' attrs={{slot: `body`}}>
              {itemsMap.has(AdvancedQueryOptions.CollapseRepeated) &&
                this.renderSwitchOption({
                  item: itemsMap.get(AdvancedQueryOptions.CollapseRepeated),
                  name: t(`flows.advanced-options.collapse`),
                  tooltipText: t(`flows.advanced-options.collapse-tooltip`),
                  tooltipImg: collapseRepeatedSvg,
                })}
              {itemsMap.has(AdvancedQueryOptions.ShowCustomEvents) &&
                this.renderSwitchOption({
                  item: itemsMap.get(AdvancedQueryOptions.ShowCustomEvents),
                  name: t(`flows.advanced-options.custom-events`),
                  tooltipText: t(`flows.advanced-options.custom-events-tooltip`),
                  tooltipImg: customEventsSvg,
                })}
              {itemsMap.has(AdvancedQueryOptions.TimePercentiles) &&
                this.renderSwitchOption({
                  item: itemsMap.get(AdvancedQueryOptions.TimePercentiles),
                  name: t(`flows.advanced-options.time-percentiles`),
                })}
              {itemsMap.has(AdvancedQueryOptions.AlwaysBreakdown) &&
                this.renderSwitchOption({
                  item: itemsMap.get(AdvancedQueryOptions.AlwaysBreakdown),
                  name: t(`flows.advanced-options.breakdown`),
                })}
            </div>
          </SelectEl>
        );
      },
    };
  }

  renderSwitchOption({
    item,
    name,
    tooltipText,
    tooltipImg,
  }: {
    item: AdvancedSettingsItem;
    name: string;
    tooltipText?: string;
    tooltipImg?: string;
  }) {
    return (
      <div sel='.option-container'>
        <SwitchEl
          attrs={{on: item.toggled}}
          on={{change: (ev) => this.handleOptionChange(ev.detail.on, item.option)}}
        />
        {tooltipText && (
          <TooltipEl
            attrs={{
              placement: Placement.Left,
              offset: 10,
              'max-width': `200px`,
              text: tooltipText,
              'image-src': tooltipImg,
            }}
          />
        )}
        <span>{name}</span>
      </div>
    );
  }

  handleOptionChange(toggled: boolean, option: AdvancedQueryOptions) {
    const event: FlowsAdvancedSettingsChangeEvent = new CustomEvent(`change`, {detail: {toggled, option}});
    this.dispatchEvent(event);
  }
}

export const FlowsAdvancedSettingsEl = defineAndMakeJsxFactory<FlowsAdvancedSettingsJsxProps>(
  `flows-advanced-settings`,
  FlowsAdvancedSettings,
);
export const FlowsAdvancedSettingsReact = panel2react<FlowsAdvancedSettingsJsxProps>(
  `flows-advanced-settings`,
  FlowsAdvancedSettings,
);
